import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { streamAssistantChat } from "@/lib/stream";
import { useAuth } from "@/contexts/AuthContext";
import AiMarkdown from "@/components/AiMarkdown";
import VoiceRecorder from "@/components/VoiceRecorder";
import { AttachButton, PiecesChips, retirerPiece } from "@/components/Attachments";
import { Apprentissage, stripRetenir } from "@/components/ZoeLearn";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, X, Send, Loader2, Maximize2, Trash2, Wrench } from "lucide-react";

const STORAGE_KEY = "oscop_floating_messages";
const SESSION_KEY = "oscop_floating_session";
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function AiFloatingWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const [pieces, setPieces] = useState([]);
  const [streamingText, setStreamingText] = useState("");
  const scrollRef = useRef(null);

  // Hide widget on public, auth, and assistant pages
  const hiddenRoutes = ["/", "/login", "/register", "/app/assistant", "/app/lili"];
  const onVerify = location.pathname.startsWith("/verify");
  const isHidden = !user || hiddenRoutes.includes(location.pathname) || onVerify;

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) setMessages(JSON.parse(raw));
      const sid = sessionStorage.getItem(SESSION_KEY);
      if (sid) setSessionId(sid);
    } catch {}
  }, []);

  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-30))); } catch {}
  }, [messages]);
  useEffect(() => {
    if (sessionId) sessionStorage.setItem(SESSION_KEY, sessionId);
  }, [sessionId]);
  useEffect(() => {
    if (open) scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending, open]);

  if (isHidden) return null;

  const send = async (q) => {
    if (!q.trim() || sending) return;
    const token = localStorage.getItem("oscopge_token");
    const jointes = pieces;
    setMessages((m) => [...m, { role: "user", content: q.trim(), pieces: jointes }]);
    setPieces([]);
    setInput("");
    setSending(true);
    setStreamingText("");
    let acc = "";
    try {
      await streamAssistantChat({
        token, baseUrl: BASE_URL,
        body: { session_id: sessionId || undefined, message: q.trim(), mode: "chat", pieces: jointes.map((p) => p.id) },
        onSession: (sid) => setSessionId(sid),
        onDelta: (t) => { acc += t; setStreamingText(acc); },
      });
    } catch (err) {
      if (err.name !== "AbortError") toast.error(err.message || "Erreur IA");
    } finally {
      if (acc) setMessages((m) => [...m, { role: "assistant", content: acc }]);
      setStreamingText("");
      setSending(false);
    }
  };

  const clear = () => {
    setMessages([]);
    setPieces([]);
    setSessionId(null);
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(SESSION_KEY);
  };

  const expand = () => {
    navigate("/app/assistant");
    setOpen(false);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-float-open"
          title="ZOE — votre assistante O'SCOP GE"
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full bg-gradient-to-br from-[#C5A059] to-[#A88846] text-white shadow-xl hover:shadow-2xl flex items-center justify-center z-40 transition-transform hover:scale-110"
        >
          <Sparkles className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-6 right-6 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-3rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden z-40"
          data-testid="ai-float-panel"
        >
          <header className="bg-gradient-to-r from-[#C5A059] to-[#A88846] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              <div>
                <div className="text-sm font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>ZOE — votre assistante O'SCOP GE</div>
                <div className="text-[10px] opacity-90">Experte O'SCOP GE</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {["admin", "gestionnaire_ge"].includes(user?.role) && (
                <button onClick={() => { setOpen(false); navigate("/app/lili"); }} title="ZOE — Administration" data-testid="ai-float-admin" className="h-7 px-2 rounded hover:bg-white/15 flex items-center gap-1 text-[11px]"><Wrench className="h-3.5 w-3.5" />Admin</button>
              )}
              <button onClick={clear} title="Effacer" className="h-7 w-7 rounded hover:bg-white/15 flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
              <button onClick={expand} title="Agrandir" data-testid="ai-float-expand" className="h-7 w-7 rounded hover:bg-white/15 flex items-center justify-center"><Maximize2 className="h-3.5 w-3.5" /></button>
              <button onClick={() => setOpen(false)} data-testid="ai-float-close" title="Fermer" className="h-7 w-7 rounded hover:bg-white/15 flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
          </header>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50">
            {messages.length === 0 ? (
              <div className="text-center py-8 text-xs text-slate-500">
                <div className="h-10 w-10 mx-auto rounded-full bg-[#FDF9F1] border border-[#C5A059]/30 flex items-center justify-center mb-2">
                  <Sparkles className="h-5 w-5 text-[#C5A059]" />
                </div>
                <p className="font-medium text-slate-700">Besoin d'aide ?</p>
                <p className="mt-1">Posez votre question sur O'SCOP GE, droit du travail, conventions L1253…</p>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[90%] rounded-lg px-3 py-2 text-xs ${m.role === "user" ? "bg-slate-900 text-white" : "bg-white text-slate-900 border border-slate-200"}`}>
                    {m.role === "user" ? <div className="whitespace-pre-wrap">{m.content}{m.pieces?.length > 0 && <div className="mt-1"><PiecesChips pieces={m.pieces} dark testId={`ai-float-msg-${i}`} /></div>}</div> : <><AiMarkdown>{stripRetenir(m.content).texte}</AiMarkdown><Apprentissage contexte="conseil" sessionId={sessionId} messageId={`w-${i}`} reponse={m.content} question={messages.slice(0, i).reverse().find((x) => x.role === "user")?.content} propositions={stripRetenir(m.content).propositions} testId={`ai-float-learn-${i}`} /></>}
                  </div>
                </div>
              ))
            )}
            {sending && !streamingText && (
              <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-lg px-3 py-2"><Loader2 className="h-3 w-3 animate-spin text-[#C5A059]" /></div></div>
            )}
            {streamingText && (
              <div className="flex justify-start">
                <div className="max-w-[90%] rounded-lg px-3 py-2 text-xs bg-white text-slate-900 border border-[#C5A059]/40 shadow-sm">
                  <AiMarkdown>{stripRetenir(streamingText).texte}</AiMarkdown>
                  <span className="inline-block w-1 h-3 ml-0.5 bg-[#C5A059] animate-pulse align-middle"></span>
                </div>
              </div>
            )}
          </div>

          {pieces.length > 0 && <div className="px-2 pt-2 bg-white"><PiecesChips pieces={pieces} testId="ai-float-compose" onRemove={(p) => retirerPiece(p, setPieces)} /></div>}
          <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="p-2 border-t border-slate-200 bg-white flex gap-1 items-end">
            <AttachButton contexte="conseil" sessionId={sessionId} pieces={pieces} setPieces={setPieces} disabled={sending} testId="ai-float" />
            <VoiceRecorder size="sm" onTranscribed={(t) => setInput((v) => (v ? v + " " : "") + t)} />
            <Textarea
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Votre question…"
              data-testid="ai-float-input"
              className="resize-none text-xs min-h-[36px]"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
            />
            <Button type="submit" disabled={sending || !input.trim()} data-testid="ai-float-send" className="bg-[#C5A059] hover:bg-[#A88846] text-white h-9 w-9 p-0">
              <Send className="h-3.5 w-3.5" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
